import React, { SetStateAction, useState, useEffect } from 'react';

import {
  Typography,
  Button,
  Box,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  Modal,
  CircularProgress,
  Switch,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { IUserDocumentTemplate } from '@homeporter/types';
import { IProfile, IUserSettings } from '../../../global/interfaces';
import { StyledModal, useDocumentTemplateStyles } from './styles';
import DocumentTemplateIcon from '../../../icons/DocumentTemplateIcon';
interface IProps {
  profile: IProfile | undefined;
  userSettings: IUserSettings | undefined;
  documentTemplates: IUserDocumentTemplate[];
  setDocumentTemplates: (documentTemplates: IUserDocumentTemplate[]) => void;
  updateDocumentTemplate: (
    documentTemplate: IUserDocumentTemplate,
  ) => Promise<void>;
  isModalOpen: boolean;
  handleCloseModal: React.Dispatch<SetStateAction<boolean>>;
}

const DocumentTemplates = ({
  profile,
  userSettings,
  documentTemplates,
  setDocumentTemplates,
  updateDocumentTemplate,
  isModalOpen,
  handleCloseModal,
}: IProps) => {
  const styles = useDocumentTemplateStyles();
  const [selectedTemplate, setSelectedTemplate] =
    useState<IUserDocumentTemplate | null>(null);
  const [uploadedTemplate, setUploadedTemplate] =
    useState<IUserDocumentTemplate | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  useEffect(() => {
    if (documentTemplates.length > 0) {
      const template = documentTemplates.find(
        (t) => t.dtid === selectedTemplate?.dtid,
      );

      setSelectedTemplate(template || null);
    }
  }, [documentTemplates]);

  const downloadTemplate = () => {
    const dataStr = JSON.stringify(selectedTemplate, null, 2);
    const dataBlob = new Blob([dataStr], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${selectedTemplate?.title.replace(/\s+/g, '_')}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const uploadTemplate = (file: File | undefined) => {
    if (!file) return;

    setIsSaving(false);
    setUploadedTemplate(null);
    setSelectedTemplate(null);

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const documentTemplate = JSON.parse(event.target?.result as string);
        documentTemplate.dtid = -1;
        documentTemplate.user_id = profile?.user_id ?? 0;

        let counter = 1;
        let newTemplateName = documentTemplate.template_name;

        while (
          documentTemplates.some((t) => t.template_name === newTemplateName)
        ) {
          newTemplateName = `${documentTemplate.template_name}_${counter}`;
          counter++;
        }

        documentTemplate.template_name = newTemplateName;
        documentTemplate.title =
          documentTemplate.title + (counter > 1 ? ` ${counter - 1}` : '');

        setDocumentTemplates([...documentTemplates, documentTemplate]);
        setUploadedTemplate(documentTemplate);
        setSelectedTemplate(documentTemplate);
        console.log('Uploaded templates:', documentTemplate);
      } catch (err) {
        console.error('Error parsing JSON:', err);
      }
    };
    reader.readAsText(file);
  };

  const saveTemplate = async () => {
    if (!uploadedTemplate) return;

    try {
      setIsSaving(true);
      await updateDocumentTemplate(uploadedTemplate);
      setUploadedTemplate(null);

      setSelectedTemplate(null);
      setIsSaving(false);
    } catch (error: any) {
      console.error('Save template error:', error);
      setIsSaving(false);
    }
  };

  const dropTemplate = () => {
    if (!uploadedTemplate) return;

    const filteredTemplates = documentTemplates.filter(
      (template) => template !== uploadedTemplate,
    );
    setDocumentTemplates(filteredTemplates);
    setUploadedTemplate(null);
  };

  const deleteTemplate = () => {
    alert('This function is not implemented yet');
    return;
    if (!selectedTemplate) return;

    const filteredTemplates = documentTemplates.filter(
      (template) => template !== selectedTemplate,
    );
    setDocumentTemplates(filteredTemplates);
  };

  return (
    <Modal open={isModalOpen} onClose={() => handleCloseModal(false)}>
      <StyledModal sx={{ maxHeight: 'min(75rem, calc(100vh - 5rem))' }}>
        <FormControl fullWidth sx={{ mt: '2.5rem' }}>
          <InputLabel sx={styles.inputLabel}>Select Template</InputLabel>
          <Select
            value={selectedTemplate?.dtid || ''}
            onChange={(e) => {
              setSelectedTemplate(
                documentTemplates.find((t) => t.dtid === e.target.value) ??
                  null,
              );
            }}
            sx={styles.select}
            variant="outlined">
            {documentTemplates.map((template) => (
              <MenuItem key={template.dtid} value={template.dtid}>
                {template.title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="subtitle2" sx={{ mt: '2rem' }}>
          Template Data:
        </Typography>
        <Box sx={styles.templateData}>
          <pre
            style={{
              padding: '1rem',
              margin: 0,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              flexGrow: 1,
              overflowY: 'auto',
            }}>
            {selectedTemplate &&
              selectedTemplate.sections
                .map(
                  (section, i) =>
                    `Section ${i + 1} Cards:\n${JSON.stringify(section.cards, null, 2)}\n\n`,
                )
                .join('')}
          </pre>
        </Box>
        <Box sx={styles.buttonContainer}>
          {uploadedTemplate && (
            <>
              <Button variant="outlined" color="error" onClick={dropTemplate}>
                {`Drop ${uploadedTemplate.title}`}
              </Button>{' '}
              <Button
                variant="contained"
                color="primary"
                onClick={saveTemplate}
                disabled={isSaving}>
                {isSaving ? (
                  <>
                    <CircularProgress size={'1.25rem'} sx={{ mr: '0.5rem' }} />
                    Saving...
                  </>
                ) : (
                  'Save'
                )}
              </Button>
            </>
          )}
          {userSettings?.supervisor &&
            selectedTemplate &&
            !uploadedTemplate && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  marginRight: 'auto',
                }}>
                <Typography sx={{ mr: 2 }}>Share with Organization</Typography>
                <Switch
                  checked={
                    selectedTemplate?.organization_id ===
                    profile?.organization_id
                  }
                  onChange={(e) => {
                    if (selectedTemplate) {
                      const organization_id = e.target.checked
                        ? (profile?.organization_id ?? 0)
                        : 0;

                      if (
                        selectedTemplate.user_id === profile?.user_id ||
                        organization_id !== 0 ||
                        window.confirm(
                          'This template was created by another user, it will disappear from your list if you unshare it.',
                        )
                      ) {
                        updateDocumentTemplate({
                          ...selectedTemplate,
                          organization_id,
                        });
                      }
                    }
                  }}
                />
              </Box>
            )}
          {!uploadedTemplate && (
            <>
              {selectedTemplate &&
                (selectedTemplate.user_id === profile?.user_id ||
                  userSettings?.supervisor) && (
                  <Button
                    variant="outlined"
                    component="button"
                    onClick={() => {
                      window.confirm(
                        `Are you sure you want to delete "${selectedTemplate.title}"?`,
                      ) && deleteTemplate();
                    }}
                    startIcon={<DeleteOutlineIcon sx={{ mr: '.25rem' }} />}>
                    Delete
                  </Button>
                )}
              <Button
                variant="outlined"
                component="label"
                startIcon={<DocumentTemplateIcon sx={{ mr: '.5rem' }} />}>
                Upload Template
                <input
                  type="file"
                  hidden
                  accept=".json"
                  onChange={(e) => uploadTemplate(e.target.files?.[0])}
                />
              </Button>

              <Button
                variant="contained"
                onClick={downloadTemplate}
                startIcon={<DocumentTemplateIcon sx={{ mr: '.5rem' }} />}>
                Download Template
              </Button>
            </>
          )}
        </Box>
      </StyledModal>
    </Modal>
  );
};

export default DocumentTemplates;
